/**
 * Sky Color Service
 * Calculates realistic sky colors based on sun position using atmospheric scattering
 */

import SunCalc from 'suncalc';

/**
 * Get realistic sky colors based on sun position
 * @param {Date} date - Current date/time
 * @param {number} latitude - Location latitude
 * @param {number} longitude - Location longitude
 * @returns {Object} Sky color configuration
 */
export function getRealisticSkyColors(date, latitude, longitude) {
  if (!date || !latitude || !longitude) {
    // Default fallback colors
    return {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      cardBg: 'rgba(255, 255, 255, 0.95)',
      text: '#1a202c',
      accent: '#667eea',
      phase: 'unknown'
    };
  }

  // Get sun position
  const sunPos = SunCalc.getPosition(date, latitude, longitude);
  const sunAltitudeDeg = sunPos.altitude * 180 / Math.PI; // Convert radians to degrees
  const sunAzimuthDeg = sunPos.azimuth * 180 / Math.PI;
  
  // Get sun times for special phases
  const times = SunCalc.getTimes(date, latitude, longitude);
  
  // Get current time in minutes for comparison
  const currentMinutes = date.getHours() * 60 + date.getMinutes();
  
  // Helper to convert date to minutes
  const dateToMinutes = (d) => {
    if (!d) return 0;
    return d.getHours() * 60 + d.getMinutes();
  };
  
  // Determine the phase of the day
  let phase = '';
  let colors = {};
  
  // Night (sun below -18°)
  if (sunAltitudeDeg < -18) {
    phase = 'night';
    colors = {
      background: 'linear-gradient(to bottom, #000428 0%, #004e92 100%)',
      cardBg: 'rgba(255, 255, 255, 0.92)',
      text: '#1a202c',
      accent: '#4a5568',
      glow: 'none'
    };
  }
  // Astronomical twilight (sun between -18° and -12°)
  else if (sunAltitudeDeg < -12) {
    phase = 'astronomical-twilight';
    colors = {
      background: 'linear-gradient(to bottom, #020111 0%, #191654 50%, #3d1e6d 100%)',
      cardBg: 'rgba(255, 255, 255, 0.93)',
      text: '#1a202c',
      accent: '#5a67d8',
      glow: 'none'
    };
  }
  // Nautical twilight (sun between -12° and -6°)
  else if (sunAltitudeDeg < -6) {
    phase = 'nautical-twilight';
    colors = {
      background: 'linear-gradient(to bottom, #090979 0%, #3a3985 40%, #7e5a7e 80%, #c86dd7 100%)',
      cardBg: 'rgba(255, 255, 255, 0.94)',
      text: '#1a202c',
      accent: '#7e5a7e',
      glow: 'subtle'
    };
  }
  // Civil twilight (sun between -6° and 0°)
  else if (sunAltitudeDeg < 0) {
    phase = 'civil-twilight';
    const twilightProgress = (sunAltitudeDeg + 6) / 6; // 0 at -6°, 1 at 0°
    colors = {
      background: `linear-gradient(to bottom, 
        hsl(220, 60%, ${15 + twilightProgress * 10}%) 0%, 
        hsl(230, 50%, ${25 + twilightProgress * 15}%) 30%, 
        hsl(260, 40%, ${40 + twilightProgress * 20}%) 60%, 
        hsl(20, 80%, ${60 + twilightProgress * 20}%) 100%)`,
      cardBg: 'rgba(255, 255, 255, 0.94)',
      text: '#1a202c',
      accent: '#9f7aea',
      glow: 'medium'
    };
  }
  // Sunrise/Sunset (sun between 0° and 6°)
  else if (sunAltitudeDeg < 6) {
    phase = 'golden-hour';
    const goldenProgress = sunAltitudeDeg / 6; // 0 at horizon, 1 at 6°
    
    // Check if it's morning or evening
    const isMorning = currentMinutes < 720; // Before noon
    
    if (isMorning) {
      // Sunrise colors
      colors = {
        background: `linear-gradient(to bottom, 
          hsl(240, 30%, ${30 + goldenProgress * 20}%) 0%, 
          hsl(280, 40%, ${40 + goldenProgress * 20}%) 25%, 
          hsl(330, 60%, ${50 + goldenProgress * 20}%) 50%, 
          hsl(20, 90%, ${60 + goldenProgress * 15}%) 75%, 
          hsl(40, 100%, ${65 + goldenProgress * 15}%) 100%)`,
        cardBg: 'rgba(255, 255, 255, 0.93)',
        text: '#1a202c',
        accent: '#f687b3',
        glow: 'strong'
      };
    } else {
      // Sunset colors
      colors = {
        background: `linear-gradient(to bottom, 
          hsl(220, 40%, ${35 + goldenProgress * 15}%) 0%, 
          hsl(260, 50%, ${45 + goldenProgress * 15}%) 25%, 
          hsl(10, 80%, ${55 + goldenProgress * 15}%) 50%, 
          hsl(30, 90%, ${60 + goldenProgress * 15}%) 75%, 
          hsl(45, 100%, ${70 + goldenProgress * 10}%) 100%)`,
        cardBg: 'rgba(255, 255, 255, 0.93)',
        text: '#1a202c',
        accent: '#ed8936',
        glow: 'strong'
      };
    }
  }
  // Early morning / Late afternoon (sun between 6° and 30°)
  else if (sunAltitudeDeg < 30) {
    const isMorning = currentMinutes < 720;
    phase = isMorning ? 'morning' : 'afternoon';
    const dayProgress = (sunAltitudeDeg - 6) / 24; // 0 at 6°, 1 at 30°
    
    if (isMorning) {
      // Morning colors
      colors = {
        background: `linear-gradient(to bottom, 
          hsl(200, 80%, ${45 + dayProgress * 20}%) 0%, 
          hsl(190, 70%, ${55 + dayProgress * 15}%) 40%, 
          hsl(180, 60%, ${65 + dayProgress * 10}%) 70%, 
          hsl(170, 50%, ${75 + dayProgress * 5}%) 100%)`,
        cardBg: 'rgba(255, 255, 255, 0.92)',
        text: '#1a202c',
        accent: '#4299e1',
        glow: 'light'
      };
    } else {
      // Afternoon colors
      colors = {
        background: `linear-gradient(to bottom, 
          hsl(200, 70%, ${50 + dayProgress * 15}%) 0%, 
          hsl(190, 60%, ${60 + dayProgress * 10}%) 40%, 
          hsl(50, 70%, ${70 + dayProgress * 5}%) 70%, 
          hsl(40, 80%, ${75 + dayProgress * 5}%) 100%)`,
        cardBg: 'rgba(255, 255, 255, 0.92)',
        text: '#1a202c',
        accent: '#d69e2e',
        glow: 'medium'
      };
    }
  }
  // Midday (sun above 30°)
  else {
    phase = 'midday';
    const middayProgress = Math.min(1, (sunAltitudeDeg - 30) / 40); // Peaks at 70°
    
    colors = {
      background: `linear-gradient(to bottom, 
        hsl(200, 100%, ${40 + middayProgress * 15}%) 0%, 
        hsl(195, 90%, ${50 + middayProgress * 15}%) 30%, 
        hsl(190, 80%, ${60 + middayProgress * 10}%) 60%, 
        hsl(185, 70%, ${70 + middayProgress * 10}%) 100%)`,
      cardBg: 'rgba(255, 255, 255, 0.90)',
      text: '#1a202c',
      accent: '#3182ce',
      glow: 'none'
    };
  }
  
  // Add phase information and sun data
  return {
    ...colors,
    phase,
    sunAltitude: sunAltitudeDeg,
    sunAzimuth: sunAzimuthDeg,
    isDaytime: sunAltitudeDeg > 0,
    isGoldenHour: sunAltitudeDeg >= 0 && sunAltitudeDeg <= 6,
    times: {
      sunrise: times.sunrise,
      sunset: times.sunset,
      solarNoon: times.solarNoon,
      nadir: times.nadir,
      dawn: times.dawn,
      dusk: times.dusk
    }
  };
}

/**
 * Get location coordinates from ZIP code
 * @param {string} zipCode - US ZIP code
 * @returns {Object} Latitude and longitude
 */
export function getLocationFromZip(zipCode) {
  // Common US ZIP codes with coordinates
  // This could be expanded or use an API
  const zipCoordinates = {
    '07039': { lat: 40.8168, lng: -74.1285 }, // Livingston, NJ
    '10001': { lat: 40.7506, lng: -73.9972 }, // New York, NY
    '90210': { lat: 34.1031, lng: -118.4105 }, // Beverly Hills, CA
    '60601': { lat: 41.8858, lng: -87.6181 }, // Chicago, IL
    '33101': { lat: 25.7783, lng: -80.1978 }, // Miami, FL
    '98101': { lat: 47.6114, lng: -122.3305 }, // Seattle, WA
    '94102': { lat: 37.7795, lng: -122.4187 }, // San Francisco, CA
    '30301': { lat: 33.7537, lng: -84.3863 }, // Atlanta, GA
    '02101': { lat: 42.3584, lng: -71.0598 }, // Boston, MA
    '48201': { lat: 42.3314, lng: -83.0458 }  // Detroit, MI
  };
  
  return zipCoordinates[zipCode] || { lat: 40.8168, lng: -74.1285 }; // Default to NJ
}

/**
 * Interpolate between two colors
 * @param {string} color1 - Start color (hex)
 * @param {string} color2 - End color (hex)
 * @param {number} factor - Interpolation factor (0-1)
 * @returns {string} Interpolated color
 */
export function interpolateColor(color1, color2, factor) {
  // Convert hex to RGB
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };
  
  const c1 = hexToRgb(color1);
  const c2 = hexToRgb(color2);
  
  if (!c1 || !c2) return color1;
  
  const r = Math.round(c1.r + (c2.r - c1.r) * factor);
  const g = Math.round(c1.g + (c2.g - c1.g) * factor);
  const b = Math.round(c1.b + (c2.b - c1.b) * factor);
  
  return `rgb(${r}, ${g}, ${b})`;
}

/**
 * Create elegant gradient blending theme color with sky colors
 * @param {string} themeColor - Brand theme color
 * @param {Object} skyColors - Sky color configuration
 * @returns {string} Blended gradient
 */
export function createElegantGradient(themeColor, skyColors) {
  // Default fallback gradient
  if (!skyColors || !skyColors.background) {
    const fallbackTheme = themeColor || '#001f3f';
    return `linear-gradient(180deg, ${fallbackTheme} 0%, ${fallbackTheme}dd 50%, ${fallbackTheme}88 100%)`;
  }
  
  // If no theme color, return sky gradient as-is
  if (!themeColor) {
    return skyColors.background;
  }
  
  // Convert theme color to RGB for blending
  const themeRgb = hexToRgb(themeColor);
  if (!themeRgb) {
    // If theme color can't be parsed, return sky gradient
    return skyColors.background;
  }
  
  // Extract the gradient content (everything after 'linear-gradient(to bottom,' or similar)
  let skyGradientContent = skyColors.background;
  
  // Remove the linear-gradient wrapper and direction
  skyGradientContent = skyGradientContent.replace(/^linear-gradient\([^,]+,\s*/, '');
  skyGradientContent = skyGradientContent.replace(/\)$/, '');
  
  // Create a smooth blend from theme color to sky gradient
  // Theme color dominates the top (0-30%), then blends into sky colors
  const gradient = `linear-gradient(180deg,
    rgb(${themeRgb.r}, ${themeRgb.g}, ${themeRgb.b}) 0%,
    rgba(${themeRgb.r}, ${themeRgb.g}, ${themeRgb.b}, 0.9) 8%,
    rgba(${themeRgb.r}, ${themeRgb.g}, ${themeRgb.b}, 0.7) 15%,
    rgba(${themeRgb.r}, ${themeRgb.g}, ${themeRgb.b}, 0.4) 22%,
    rgba(${themeRgb.r}, ${themeRgb.g}, ${themeRgb.b}, 0.15) 28%,
    ${skyGradientContent}`;
  
  // Clean up extra whitespace and return
  return gradient.replace(/\s+/g, ' ').trim();
}

/**
 * Convert hex color to RGB object
 */
function hexToRgb(hex) {
  if (!hex) return null;
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}

/**
 * Determine if background is dark or light
 * @param {Object} skyColors - Sky color configuration  
 * @returns {boolean} True if dark background
 */
export function isDarkBackground(skyColors) {
  const darkPhases = ['night', 'astronomical-twilight', 'nautical-twilight', 'civil-twilight'];
  return darkPhases.includes(skyColors?.phase);
}

/**
 * Parse color to RGB object from various formats
 * @param {string} color - Color in hex, rgb, rgba, or hsl format
 * @returns {Object} RGB object with r, g, b values (0-255)
 */
export function parseColorToRgb(color) {
  if (!color) return { r: 128, g: 128, b: 128 };
  
  // Remove spaces
  color = color.replace(/\s/g, '');
  
  // Hex format
  if (color.startsWith('#')) {
    return hexToRgb(color);
  }
  
  // RGB/RGBA format
  if (color.startsWith('rgb')) {
    const match = color.match(/rgba?\((\d+),(\d+),(\d+)/);
    if (match) {
      return {
        r: parseInt(match[1]),
        g: parseInt(match[2]),
        b: parseInt(match[3])
      };
    }
  }
  
  // HSL format (convert to RGB)
  if (color.startsWith('hsl')) {
    const match = color.match(/hsla?\((\d+),(\d+)%,(\d+)%/);
    if (match) {
      const h = parseInt(match[1]) / 360;
      const s = parseInt(match[2]) / 100;
      const l = parseInt(match[3]) / 100;
      return hslToRgb(h, s, l);
    }
  }
  
  return { r: 128, g: 128, b: 128 }; // Default gray
}

/**
 * Convert HSL to RGB
 */
function hslToRgb(h, s, l) {
  let r, g, b;
  
  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };
    
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }
  
  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255)
  };
}

/**
 * Calculate perceived luminance of a color (0-1)
 * @param {string} color - Color in any format
 * @returns {number} Luminance value (0 = dark, 1 = light)
 */
export function getColorLuminance(color) {
  const rgb = parseColorToRgb(color);
  
  // Use relative luminance formula (ITU-R BT.709)
  const luminance = (0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b) / 255;
  
  return luminance;
}

/**
 * Get optimal text color based on background
 * @param {string} backgroundColor - Background color
 * @param {Object} options - Options for contrast colors
 * @returns {string} Optimal text color
 */
export function getContrastColor(backgroundColor, options = {}) {
  const luminance = getColorLuminance(backgroundColor);
  
  // Adjust threshold based on context
  const threshold = options.threshold || 0.43; // Slightly lower threshold for better contrast
  
  if (luminance > threshold) {
    // Light background - use dark text
    return options.darkColor || 'rgba(15, 23, 42, 0.95)'; // Dark slate
  } else {
    // Dark background - use light text
    return options.lightColor || 'rgba(248, 250, 252, 0.95)'; // Near white
  }
}

/**
 * Get text style with shadows for better readability
 * @param {string} backgroundColor - Background color
 * @returns {Object} Text style object
 */
export function getTextStyle(backgroundColor) {
  const luminance = getColorLuminance(backgroundColor);
  const textColor = getContrastColor(backgroundColor);
  
  return {
    color: textColor,
    textShadow: luminance > 0.5 
      ? '0 1px 2px rgba(0,0,0,0.15)' // Light bg: subtle dark shadow
      : '0 1px 3px rgba(0,0,0,0.4)', // Dark bg: stronger shadow for definition
    fontWeight: luminance > 0.6 ? '600' : '500' // Bolder on very light backgrounds
  };
}

/**
 * Extract dominant color from gradient at position
 * @param {string} gradient - CSS gradient string
 * @param {number} position - Position in gradient (0-1)
 * @returns {string} Approximate color at position
 */
export function getGradientColorAtPosition(gradient, position) {
  if (!gradient) return '#808080';
  
  // Extract color stops from gradient
  const colorStops = [];
  const regex = /((?:rgb|rgba|hsl|hsla|#)[^,)]+(?:\([^)]+\))?)\s+(\d+)%/g;
  let match;
  
  while ((match = regex.exec(gradient)) !== null) {
    colorStops.push({
      color: match[1],
      position: parseInt(match[2]) / 100
    });
  }
  
  if (colorStops.length === 0) {
    // Try to extract colors without percentages
    const simpleRegex = /((?:rgb|rgba|hsl|hsla|#)[^,)]+(?:\([^)]+\))?)/g;
    const colors = gradient.match(simpleRegex);
    if (colors && colors.length > 0) {
      // Distribute colors evenly
      colors.forEach((color, index) => {
        colorStops.push({
          color: color,
          position: index / (colors.length - 1)
        });
      });
    }
  }
  
  if (colorStops.length === 0) return '#808080';
  
  // Find the two stops around our position
  for (let i = 0; i < colorStops.length - 1; i++) {
    if (position >= colorStops[i].position && position <= colorStops[i + 1].position) {
      // For simplicity, return the nearest color
      const midPoint = (colorStops[i].position + colorStops[i + 1].position) / 2;
      return position < midPoint ? colorStops[i].color : colorStops[i + 1].color;
    }
  }
  
  // If position is outside range, return nearest edge
  return position < 0.5 ? colorStops[0].color : colorStops[colorStops.length - 1].color;
}

const skyColorService = {
  getRealisticSkyColors,
  getLocationFromZip,
  interpolateColor,
  createElegantGradient,
  isDarkBackground,
  parseColorToRgb,
  getColorLuminance,
  getContrastColor,
  getTextStyle,
  getGradientColorAtPosition
};

export default skyColorService;